import { useState, useEffect, useCallback } from 'react';
import { Plus, Edit2, Trash2, X, Search, ChevronDown, ChevronUp, Package } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrderBook = ({ type }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [expandedOrder, setExpandedOrder] = useState(null);

  const isTL = type === 'TL';
  const accentColor = isTL ? 'cyan' : 'indigo';
  const endpoint = isTL ? 'tl' : 'swatch';
  const unit = isTL ? 'm' : 'pcs';

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (searchQuery) params.append('client_name', searchQuery);
      
      const response = await axios.get(`${API}/${endpoint}/orders?${params}`);
      setOrders(response.data);
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  }, [endpoint, statusFilter, searchQuery]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleDelete = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this order?')) return;
    try {
      await axios.delete(`${API}/${endpoint}/orders/${orderId}`);
      toast.success('Order deleted');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to delete order');
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'Completed':
        return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'Partial':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200';
    }
  };

  // For TL multi-item orders, search across items
  const filteredOrders = orders.filter(order => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    
    if (order.client_name.toLowerCase().includes(query)) return true;
    
    // For TL multi-item orders
    if (isTL && order.items) {
      return order.items.some(item => 
        item.quality_name.toLowerCase().includes(query)
      );
    }
    
    // Legacy single-item orders
    if (order.quality_name?.toLowerCase().includes(query)) return true;
    
    return false;
  });

  // Get order totals (handles both multi-item and legacy)
  const getOrderTotals = (order) => {
    if (isTL && order.items) {
      return {
        ordered: order.total_ordered || order.items.reduce((sum, i) => sum + (i.quantity_ordered || 0), 0),
        sent: order.total_sent || order.items.reduce((sum, i) => sum + (i.quantity_sent || 0), 0),
        balance: order.total_balance || order.items.reduce((sum, i) => sum + (i.balance || 0), 0),
        itemCount: order.items.length
      };
    }
    return {
      ordered: order.quantity_ordered || 0,
      sent: order.quantity_sent || 0,
      balance: order.balance || 0,
      itemCount: 1
    };
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex items-center gap-3 flex-1 w-full sm:w-auto">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search by client or quality..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 h-10 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-cyan-500"
              data-testid="order-search-input"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-10 px-3 rounded-lg border border-slate-200 text-sm"
            data-testid="order-status-filter"
          >
            <option value="">All Status</option>
            <option value="Pending">Pending</option>
            <option value="Partial">Partial</option>
            <option value="Completed">Completed</option>
          </select>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className={`flex items-center gap-2 px-4 py-2.5 bg-${accentColor}-600 text-white rounded-lg hover:bg-${accentColor}-700 shadow-md transition-colors font-medium`}
          data-testid="add-order-button"
        >
          <Plus className="w-5 h-5" />
          Add Order
        </button>
      </div>

      {/* Orders List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-3 border-cyan-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading orders...</p>
        </div>
      ) : filteredOrders.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
          <p className="text-slate-500">No orders found</p>
          <p className="text-sm text-slate-400 mt-1">Add your first order to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredOrders.map((order) => {
            const totals = getOrderTotals(order);
            const isExpanded = expandedOrder === order.id;
            const hasMultipleItems = isTL && order.items && order.items.length > 1;
            
            return (
              <div
                key={order.id}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden"
                data-testid={`order-row-${order.id}`}
              >
                {/* Order Header */}
                <div
                  className={`px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors ${
                    isExpanded ? 'border-b border-slate-200' : ''
                  }`}
                  onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                >
                  <div className="flex items-center gap-4 flex-1">
                    <div className={`w-10 h-10 rounded-full bg-${accentColor}-100 flex items-center justify-center flex-shrink-0`}>
                      <span className={`text-${accentColor}-700 font-semibold`}>
                        {order.client_name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900 truncate">{order.client_name}</h3>
                        {hasMultipleItems && (
                          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">
                            {totals.itemCount} items
                          </span>
                        )}
                      </div>
                      {!isTL || !order.items ? (
                        <p className="text-sm text-slate-500 truncate">{order.quality_name}</p>
                      ) : order.items.length === 1 ? (
                        <p className="text-sm text-slate-500 truncate">{order.items[0].quality_name}</p>
                      ) : (
                        <p className="text-sm text-slate-500 truncate">
                          {order.items.slice(0, 2).map(i => i.quality_name).join(', ')}
                          {order.items.length > 2 && ` +${order.items.length - 2} more`}
                        </p>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 sm:gap-6">
                    <div className="hidden sm:block text-right">
                      <div className="text-xs text-slate-500 uppercase">Ordered</div>
                      <div className="font-mono text-slate-900">{totals.ordered}{unit}</div>
                    </div>
                    <div className="hidden sm:block text-right">
                      <div className="text-xs text-slate-500 uppercase">Sent</div>
                      <div className="font-mono text-slate-900">{totals.sent}{unit}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-slate-500 uppercase">Balance</div>
                      <div className={`font-mono font-semibold ${totals.balance > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                        {totals.balance}{unit}
                      </div>
                    </div>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${getStatusBadgeClass(order.status)}`}>
                      {order.status}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); setEditingOrder(order); }}
                        className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                        title="Edit"
                        data-testid={`edit-order-${order.id}`}
                      >
                        <Edit2 className="w-4 h-4 text-slate-600" />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDelete(order.id); }}
                        className="p-2 hover:bg-rose-50 rounded-lg transition-colors"
                        title="Delete"
                        data-testid={`delete-order-${order.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-rose-600" />
                      </button>
                      {hasMultipleItems && (
                        isExpanded ? <ChevronUp className="w-5 h-5 text-slate-400" /> : <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>
                </div>

                {/* Expanded Items List */}
                {isExpanded && isTL && order.items && order.items.length > 0 && (
                  <div className="bg-slate-50">
                    <table className="w-full">
                      <thead>
                        <tr className="text-xs text-slate-500 uppercase border-b border-slate-200">
                          <th className="px-4 py-2 text-left">Quality</th>
                          <th className="px-4 py-2 text-right">Ordered</th>
                          <th className="px-4 py-2 text-right">Sent</th>
                          <th className="px-4 py-2 text-right">Balance</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {order.items.map((item, idx) => (
                          <tr key={item.id || idx} className="text-sm">
                            <td className="px-4 py-2.5 font-medium text-slate-700">{item.quality_name}</td>
                            <td className="px-4 py-2.5 text-right font-mono">{item.quantity_ordered}{unit}</td>
                            <td className="px-4 py-2.5 text-right font-mono">{item.quantity_sent || 0}{unit}</td>
                            <td className={`px-4 py-2.5 text-right font-mono font-semibold ${
                              (item.balance || item.quantity_ordered - (item.quantity_sent || 0)) > 0 ? 'text-amber-600' : 'text-emerald-600'
                            }`}>
                              {item.balance ?? (item.quantity_ordered - (item.quantity_sent || 0))}{unit}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {(showAddModal || editingOrder) && (
        isTL ? (
          <TLOrderModal
            order={editingOrder}
            onClose={() => {
              setShowAddModal(false);
              setEditingOrder(null);
            }}
            onSuccess={() => {
              setShowAddModal(false);
              setEditingOrder(null);
              fetchOrders();
            }}
          />
        ) : (
          <SwatchOrderModal
            order={editingOrder}
            onClose={() => {
              setShowAddModal(false);
              setEditingOrder(null);
            }}
            onSuccess={() => {
              setShowAddModal(false);
              setEditingOrder(null);
              fetchOrders();
            }}
          />
        )
      )}
    </div>
  );
};

// TL Order Modal with Multi-Item Support
const TLOrderModal = ({ order, onClose, onSuccess }) => {
  const [clientName, setClientName] = useState(order?.client_name || '');
  const [items, setItems] = useState(
    order?.items?.length > 0 
      ? order.items.map(i => ({ 
          id: i.id, 
          quality_name: i.quality_name, 
          quantity_ordered: i.quantity_ordered,
          quantity_sent: i.quantity_sent || 0
        }))
      : order?.quality_name 
        ? [{ id: '1', quality_name: order.quality_name, quantity_ordered: order.quantity_ordered, quantity_sent: order.quantity_sent || 0 }]
        : [{ id: '1', quality_name: '', quantity_ordered: '' }]
  );
  const [notes, setNotes] = useState(order?.notes || '');
  const [loading, setLoading] = useState(false);

  const addItem = () => {
    setItems([...items, { id: String(Date.now()), quality_name: '', quantity_ordered: '' }]);
  };

  const removeItem = (index) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index, field, value) => {
    setItems(items.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate
    const validItems = items.filter(i => i.quality_name.trim() && i.quantity_ordered);
    if (validItems.length === 0) {
      toast.error('Please add at least one quality with quantity');
      return;
    }
    
    setLoading(true);

    try {
      const payload = {
        client_name: clientName,
        items: validItems.map(i => ({
          id: i.id,
          quality_name: i.quality_name.trim(),
          quantity_ordered: parseFloat(i.quantity_ordered),
          quantity_sent: i.quantity_sent || 0,
          balance: parseFloat(i.quantity_ordered) - (i.quantity_sent || 0)
        })),
        notes
      };

      if (order) {
        await axios.patch(`${API}/tl/orders/${order.id}`, payload);
        toast.success('Order updated');
      } else {
        await axios.post(`${API}/tl/orders`, payload);
        toast.success('Order created');
      }
      onSuccess();
    } catch (error) {
      toast.error(order ? 'Failed to update order' : 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between flex-shrink-0">
          <h2 className="text-xl font-semibold text-slate-900">
            {order ? 'Edit Order' : 'Add New Order'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
          <div className="p-6 space-y-5">
            {/* Client Name */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
                Client Name <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                required
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                className="w-full h-11 rounded-lg border border-slate-200 px-3 text-sm focus:ring-2 focus:ring-cyan-500"
                placeholder="Enter client name"
                data-testid="order-client-input"
              />
            </div>

            {/* Items Section */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Order Items <span className="text-rose-500">*</span>
                </label>
                <button
                  type="button"
                  onClick={addItem}
                  className="text-sm text-cyan-600 hover:text-cyan-700 font-medium flex items-center gap-1"
                  data-testid="add-item-button"
                >
                  <Plus className="w-4 h-4" />
                  Add Another Quality
                </button>
              </div>
              
              <div className="space-y-3">
                {items.map((item, index) => (
                  <div key={item.id} className="flex gap-3 items-start p-3 bg-slate-50 rounded-lg">
                    <div className="flex-1">
                      <input
                        type="text"
                        required
                        value={item.quality_name}
                        onChange={(e) => updateItem(index, 'quality_name', e.target.value)}
                        className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:ring-2 focus:ring-cyan-500 bg-white"
                        placeholder="Quality/Fabric name"
                        data-testid={`order-quality-input-${index}`}
                      />
                    </div>
                    <div className="w-32">
                      <input
                        type="number"
                        required
                        min="0"
                        step="0.01"
                        value={item.quantity_ordered}
                        onChange={(e) => updateItem(index, 'quantity_ordered', e.target.value)}
                        className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:ring-2 focus:ring-cyan-500 bg-white"
                        placeholder="Meters"
                        data-testid={`order-quantity-input-${index}`}
                      />
                    </div>
                    {items.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItem(index)}
                        className="p-2 hover:bg-rose-50 rounded-lg transition-colors"
                        data-testid={`remove-item-${index}`}
                      >
                        <X className="w-4 h-4 text-rose-500" />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              {/* Total Summary */}
              {items.length > 1 && (
                <div className="mt-3 p-3 bg-cyan-50 rounded-lg flex justify-between items-center">
                  <span className="text-sm font-medium text-cyan-700">Total Order</span>
                  <span className="font-mono font-semibold text-cyan-700">
                    {items.reduce((sum, i) => sum + (parseFloat(i.quantity_ordered) || 0), 0).toFixed(2)} m
                  </span>
                </div>
              )}
            </div>

            {/* Notes */}
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
                Notes (Optional)
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                className="w-full h-20 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-cyan-500 resize-none"
                placeholder="Any additional notes..."
                data-testid="order-notes-input"
              />
            </div>
          </div>

          <div className="px-6 py-4 border-t border-slate-200 flex gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-white text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 font-medium disabled:opacity-50"
              data-testid="order-submit-button"
            >
              {loading ? 'Saving...' : order ? 'Update Order' : 'Create Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Swatch Order Modal (Single Item - unchanged)
const SwatchOrderModal = ({ order, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    client_name: order?.client_name || '',
    quality_name: order?.quality_name || '',
    quantity_ordered: order?.quantity_ordered || '',
    notes: order?.notes || ''
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = {
        ...formData,
        quantity_ordered: parseInt(formData.quantity_ordered)
      };

      if (order) {
        await axios.patch(`${API}/swatch/orders/${order.id}`, payload);
        toast.success('Order updated');
      } else {
        await axios.post(`${API}/swatch/orders`, payload);
        toast.success('Order created');
      }
      onSuccess();
    } catch (error) {
      toast.error(order ? 'Failed to update order' : 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full shadow-2xl">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-slate-900">
            {order ? 'Edit Order' : 'Add New Order'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg">
            <X className="w-5 h-5 text-slate-600" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
              Client Name
            </label>
            <input
              type="text"
              required
              value={formData.client_name}
              onChange={(e) => setFormData({ ...formData, client_name: e.target.value })}
              className="w-full h-11 rounded-lg border border-slate-200 px-3 text-sm focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter client name"
              data-testid="order-client-input"
            />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
              Quality Name
            </label>
            <input
              type="text"
              required
              value={formData.quality_name}
              onChange={(e) => setFormData({ ...formData, quality_name: e.target.value })}
              className="w-full h-11 rounded-lg border border-slate-200 px-3 text-sm focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter quality/fabric name"
              data-testid="order-quality-input"
            />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
              Quantity Ordered (Pieces)
            </label>
            <input
              type="number"
              required
              min="1"
              step="1"
              value={formData.quantity_ordered}
              onChange={(e) => setFormData({ ...formData, quantity_ordered: e.target.value })}
              className="w-full h-11 rounded-lg border border-slate-200 px-3 text-sm focus:ring-2 focus:ring-indigo-500"
              placeholder="Enter quantity"
              data-testid="order-quantity-input"
            />
          </div>

          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5 block">
              Notes (Optional)
            </label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="w-full h-20 rounded-lg border border-slate-200 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 resize-none"
              placeholder="Any additional notes..."
              data-testid="order-notes-input"
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-white text-slate-700 border border-slate-200 rounded-lg hover:bg-slate-50 font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 font-medium disabled:opacity-50"
              data-testid="order-submit-button"
            >
              {loading ? 'Saving...' : order ? 'Update Order' : 'Create Order'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrderBook;
